import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  IconButton,
  IconButtonProps,
  Stack,
  StackProps,
  SvgIconProps,
} from "@mui/material";
import React, { createContext, useContext, useState } from "react";
import { LoadingGuard } from "../components/LoadingGuard";

export type PublicDialogProps = Omit<DialogProps, "open" | "onClose">;

export type OpenModalProps = {
  title?: string | React.ReactNode;
  open?: boolean;
  loading?: boolean;
  toolbar?: { component: React.ReactNode; hideCloseButton?: boolean };
  content?: React.ReactNode;
  actions?: React.ReactNode;
  slotProps?: {
    dialog: PublicDialogProps;
    dialogTitle?: DialogTitleProps;
    dialogContent?: DialogContentProps;
    dialogActions?: DialogActionsProps;
    title?: StackProps | undefined;
    toolbar?: StackProps | undefined;
    closeButton?: {
      button?: IconButtonProps;
      icon?: SvgIconProps;
    };
    loadingGuard?: React.ComponentProps<typeof LoadingGuard>;
  };
};

export type DialogProviderContextType = {
  get: (id: string) => OpenModalProps | undefined;
  exists: (id: string) => boolean;
  open: (id: string, props: Omit<OpenModalProps, "open">) => void;
  update: (
    id: string,
    updater: (props: OpenModalProps) => OpenModalProps
  ) => void;
  close: (id: string) => void;
  create: (
    id: string,
    props: OpenModalProps,
    opts?: { onConflict?: "preserve" | "replace" | "throw" }
  ) => void;
  remove: (id: string) => void;
};

export const DialogProviderContext = createContext<
  DialogProviderContextType | undefined
>(undefined);

export function DialogTitleBar({
  title,
  toolbar,
  slotProps,
  closeDialog,
}: {
  title?: string | React.ReactNode;
  toolbar?: { component: React.ReactNode; hideCloseButton?: boolean };
  slotProps?: Pick<
    NonNullable<OpenModalProps["slotProps"]>,
    "title" | "toolbar" | "closeButton" | "dialogTitle"
  >;
  closeDialog: () => void;
}) {
  return (
    <DialogTitle
      display={"flex"}
      justifyContent="space-between"
      width={"100%"}
      title=""
      {...(slotProps?.dialogTitle ?? {})}
    >
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        gap={1}
        {...(slotProps?.title ?? {})}
      >
        {title}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flex={1}
        justifyContent="flex-end"
        {...(slotProps?.toolbar ?? {})}
      >
        {toolbar?.component}
        {!toolbar?.hideCloseButton && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeDialog}
            aria-label="close"
            {...(slotProps?.closeButton?.button ?? {})}
          >
            <CloseIcon {...(slotProps?.closeButton?.icon ?? {})} />
          </IconButton>
        )}
      </Stack>
    </DialogTitle>
  );
}

export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialogs, setDialogs] = useState<Record<string, OpenModalProps>>({});

  const exists = (id: string) => {
    return id in dialogs;
  };

  const openDialog = (id: string, props?: Omit<OpenModalProps, "open">) => {
    if (exists(id)) {
      setDialogs((prevDialogs) => ({
        ...prevDialogs,
        [id]: {
          ...prevDialogs[id],
          open: true,
          ...props,
        },
      }));
    } else if (props) {
      createDialog(id, {
        ...props,
        open: true,
      });
    } else {
      throw new Error(
        `Dialog with id ${id} does not exist. Provide props to create it or use createDialog.`
      );
    }
  };

  const closeDialog = (id: string) => {
    setDialogs((prevDialogs) => {
      if (exists(id)) {
        return {
          ...prevDialogs,
          [id]: {
            open: false,
          },
        };
      } else {
        throw new Error(`Dialog with id ${id} does not exist`);
      }
    });
  };

  const removeDialog = (id: string) => {
    setDialogs((prevDialogs) => {
      const { [id]: _, ...newDialogs } = prevDialogs;
      return newDialogs;
    });
  };

  const updateDialog = (
    id: string,
    updater: (props: OpenModalProps) => OpenModalProps
  ) => {
    setDialogs((prevDialogs) => {
      if (exists(id)) {
        return {
          ...prevDialogs,
          [id]: updater(prevDialogs[id]),
        };
      } else {
        console.error(`Dialog with id ${id} does not exist`, dialogs);
        throw new Error(`Dialog with id ${id} does not exist`);
      }
    });
  };

  const createDialog = (
    id: string,
    props: OpenModalProps,
    {
      onConflict = "preserve",
    }: { onConflict?: "preserve" | "replace" | "throw" } = {}
  ) => {
    if (exists(id)) {
      if (onConflict === "preserve") {
        return;
      } else if (onConflict === "replace") {
        updateDialog(id, () => props);
        return;
      } else if (onConflict === "throw") {
        throw new Error(`Dialog with id ${id} already exists`);
      }
    }

    setDialogs((prevDialogs) => ({
      ...prevDialogs,
      [id]: props,
    }));
  };

  const getDialog = (id: string) => {
    return dialogs[id];
  };

  return (
    <DialogProviderContext.Provider
      value={{
        get: getDialog,
        open: openDialog,
        close: closeDialog,
        exists,
        update: updateDialog,
        create: createDialog,
        remove: removeDialog,
      }}
    >
      {children}
      {Object.entries(dialogs).map(([id, dialog]) => {
        return (
          <Dialog
            {...(dialog.slotProps?.dialog ?? {})}
            open={dialog.open ?? false}
            key={id}
          >
            <LoadingGuard
              isLoading={dialog.loading ?? false}
              {...(dialog.slotProps?.loadingGuard ?? {})}
            >
              <DialogTitleBar
                title={dialog.title}
                toolbar={dialog.toolbar}
                slotProps={dialog.slotProps}
                closeDialog={() => closeDialog(id)}
              />
              <DialogContent {...(dialog.slotProps?.dialogContent ?? {})}>
                {dialog.content}
              </DialogContent>
              <DialogActions {...(dialog.slotProps?.dialogActions ?? {})}>
                {dialog.actions}
              </DialogActions>
            </LoadingGuard>
          </Dialog>
        );
      })}
    </DialogProviderContext.Provider>
  );
}

export function useDialog() {
  const context = useContext(DialogProviderContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
}
